import { useEffect, useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { BsCashStack, BsCheckCircleFill } from "react-icons/bs";
import { FiArrowUpRight, FiFileText, FiPlus, FiClock } from "react-icons/fi";
import { MdCancel } from "react-icons/md";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Stats from "../../../components/Stats";
import { apiService } from "../../../services/apiService";
import { plusIcon } from "../../../utils/svgs";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import { hasPermission } from "../../../utils/helper";
import "../../../styles/Access.css";
import "../../../styles/Invoices.css";
import "../../../styles/Stats.css";

const formatAmount = (value) =>
  new Intl.NumberFormat("en-PK", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const statusMeta = {
  pending: {
    className: "is_submitted",
    label: "Pending",
    icon: <FiArrowUpRight />,
  },
  new: {
    className: "is_new",
    label: "New",
    icon: <FiFileText />,
  },
  submitted: {
    className: "is_submitted",
    label: "Submitted",
    icon: <FiArrowUpRight />,
  },
  paid: {
    className: "is_paid",
    label: "Paid",
    icon: <BsCheckCircleFill />,
  },
  approved: {
    className: "is_paid",
    label: "Approved",
    icon: <BsCheckCircleFill />,
  },
  rejected: {
    className: "is_rejected",
    label: "Rejected",
    icon: <MdCancel />,
  },
};

const buildStatIcon = (icon) => <span className="finance_metric_icon">{icon}</span>;

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const canCreateInvoices = hasPermission({
    permissionSlug: FinancePermission.Create,
    menuSlug: FinanceSubModules.Invoices,
    moduleSlug: AppMainModules.finance,
  });
  const [loading, setLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    pendingApprovalsCount: 0,
    outstandingAR: 0,
    paymentsReceived: 0,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await apiService.getFinanceDashboardHome();
        if (!isMounted) return;

        const homeData = response?.data || response || {};
        const metrics = homeData.metrics || {};

        setStats({
          totalInvoices: metrics.total_invoices || 0,
          pendingApprovalsCount: metrics.pending_approval || 0,
          outstandingAR: metrics.outstanding_ar || 0,
          paymentsReceived: metrics.payments_received || 0,
        });

        // Map pending approvals to include total_amount and billing_period
        const mappedApprovals = (Array.isArray(homeData.pending_approvals) ? homeData.pending_approvals : []).map((inv) => {
          const billing_period = (inv.billing_period_from && inv.billing_period_to)
            ? `${moment(inv.billing_period_from).format("YYYY-MM-DD")} to ${moment(inv.billing_period_to).format("YYYY-MM-DD")}`
            : (inv.billing_period || "-");
          return {
            ...inv,
            total_amount: inv.total_amount || inv.net_amount || inv.gross_amount || 0,
            billing_period,
          };
        });
        setPendingApprovals(mappedApprovals);

        // Map recent payments to include invoiceIdRaw and created_at
        const mappedPayments = (Array.isArray(homeData.recent_payments) ? homeData.recent_payments : []).map((p) => ({
          ...p,
          invoiceIdRaw: p.invoice_id || p.invoiceIdRaw,
          created_at: p.created_at || p.payment_date,
        }));
        setRecentPayments(mappedPayments);

      } catch (error) {
        console.error("Finance Dashboard load failed:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const statsData = useMemo(() => [
    {
      title: "Total Invoices",
      value: formatAmount(stats.totalInvoices),
      text: <span>Invoices created</span>,
      icon: buildStatIcon(<FiFileText />),
    },
    {
      title: "Pending Approval",
      value: formatAmount(stats.pendingApprovalsCount),
      text: <span>Requires manager review</span>,
      className: "pending-delivery",
      icon: buildStatIcon(<FiClock />),
    },
    {
      title: "Outstanding AR",
      value: `PKR ${formatAmount(stats.outstandingAR)}`,
      text: <span>Active receivables</span>,
      icon: buildStatIcon(<BsCashStack />),
    },
    {
      title: "Payments Received",
      value: `PKR ${formatAmount(stats.paymentsReceived)}`,
      text: <span>Total collected cash</span>,
      className: "completed",
      icon: buildStatIcon(<BsCashStack />),
    },
  ], [stats]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div className="trip_cost_estimation_page invoices_page">
      <Breadcrumbs values={["Finance", "Dashboard"]} />

      <div className="access_view" style={{ marginBottom: "24px" }}>
        <div>
          <h2>Finance Dashboard</h2>
        </div>

        {canCreateInvoices && (
          <div className="services_buttons assets_registration_actions">
            <button
              type="button"
              onClick={() => navigate("/invoices/create")}
            >
              {plusIcon} Create Invoice
            </button>
          </div>
        )}
      </div>

      <Stats statsData={statsData} numberOfColumn={3} />

      <div style={{ marginTop: "24px", paddingLeft: "20px", paddingRight: "20px" }}>
        <Row>
          <Col md={8}>
            <div style={{ background: "#ffffff", padding: "24px", borderRadius: "12px", border: "1px solid #ECECF2", height: "100%", minHeight: "350px" }}>
              <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#3D3F5C", marginBottom: "16px" }}>Pending Approvals</h4>
              
              <div style={{ overflowX: "auto" }}>
                <table className="w-100 finance_plain_table" style={{ borderCollapse: "collapse", minWidth: "500px" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #E6E6E6" }}>
                      <th>INVOICE</th>
                      <th>CUSTOMER</th>
                      <th>PERIOD</th>
                      <th className="th_right">AMOUNT</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingApprovals.length ? pendingApprovals.map((invoice) => {
                      const statusObj = statusMeta[String(invoice?.status || "").toLowerCase()] || statusMeta.new;
                      return (
                        <tr key={invoice?.id} style={{ borderBottom: "1px solid #ECECF2" }}>
                          <td style={{ padding: "16px 10px", fontSize: "13px", color: "#4F19E2", textDecoration: "underline", cursor: "pointer", fontWeight: 500 }} onClick={() => navigate(`/invoices/details/${invoice.id}`)}>
                            {invoice?.trips_invoice_id || invoice?.invoiceId || "-"}
                          </td>
                          <td style={{ padding: "16px 10px", fontSize: "13px", color: "#3D3F5C" }}>
                            {invoice?.customer?.name || invoice?.customerName || "-"}
                          </td>
                          <td style={{ padding: "16px 10px", fontSize: "13px" }}>
                            {invoice?.billing_period_from && invoice?.billing_period_to ? (
                              <div className="d-flex align-items-center gap-1 font-monospace" style={{ fontSize: "12px" }}>
                                <span style={{ background: "#F1F5F9", color: "#334155", padding: "4px 8px", borderRadius: "6px", fontWeight: 500 }}>
                                  {moment(invoice.billing_period_from).format("YYYY-MM-DD")}
                                </span>
                                <span style={{ color: "#94A3B8", fontWeight: 600 }}>&rarr;</span>
                                <span style={{ background: "#F1F5F9", color: "#334155", padding: "4px 8px", borderRadius: "6px", fontWeight: 500 }}>
                                  {moment(invoice.billing_period_to).format("YYYY-MM-DD")}
                                </span>
                              </div>
                            ) : (
                              invoice?.billing_period || "-"
                            )}
                          </td>
                          <td style={{ padding: "16px 10px", fontSize: "13px", color: "#3D3F5C", fontWeight: 500, textAlign: "right" }}>
                            PKR {formatAmount(invoice?.total_amount)}
                          </td>
                          <td style={{ padding: "16px 10px", fontSize: "13px" }}>
                            <span className={`invoice_status ${statusObj.className}`}>
                              {statusObj.icon}
                              <span style={{ marginLeft: "4px" }}>{statusObj.label}</span>
                            </span>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: "60px 20px", color: "#888EA8", fontSize: "14px" }}>
                          No pending approval actions.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div style={{ background: "#ffffff", padding: "24px", borderRadius: "12px", border: "1px solid #ECECF2", height: "100%", minHeight: "350px" }}>
              <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#3D3F5C", marginBottom: "16px" }}>Recent Payments</h4>
              <div>
                {recentPayments.length ? recentPayments.map((payment, idx) => {
                  return (
                    <div key={payment?.id || idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderBottom: idx === recentPayments.length - 1 ? "none" : "1px solid #ECECF2" }}>
                      <div>
                        <strong style={{ fontSize: "14px", color: "#3D3F5C", display: "block", textDecoration: "underline", cursor: "pointer" }} onClick={() => navigate(`/invoices/details/${payment.invoiceIdRaw}`)}>
                          {payment?.reference_number || payment?.refNo || `TR-${payment?.id}`}
                        </strong>
                        <span style={{ fontSize: "12px", color: "#888EA8" }}>
                          {payment?.created_at ? moment(payment.created_at).format("YYYY-MM-DD") : "-"}
                        </span>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <strong style={{ fontSize: "14px", color: "#2CB814", display: "block" }}>
                          PKR {formatAmount(payment?.amount)}
                        </strong>
                        <span style={{ fontSize: "12px", color: "#888EA8" }}>
                          WH: PKR {formatAmount(payment?.withholding_tax || payment?.wh_amount || 0)}
                        </span>
                      </div>
                    </div>
                  );
                }) : (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "#888EA8", fontSize: "14px" }}>
                    No recent payments found.
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default FinanceDashboard;
